import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTenant } from "../contexts/TenantContext";

const TermsOfService = () => {
  const { tenant, getTenantLogo, getTenantName, getPrimaryColor } = useTenant();
  
  const companyName = getTenantName();
  const logoUrl = getTenantLogo();
  const primaryColor = getPrimaryColor();
  const isRoyalCert = tenant?.slug === "royalcert";
  
  // Tenant-specific content
  const tenantContent = {
    royalcert: {
      companyFullName: "RoyalCert Belgelendirme ve Gözetim Hizmetleri A.Ş.",
      email: "info@royalcert.com",
      website: "royalcert.com",
      address: "İstanbul, Türkiye"
    },
    trb: {
      companyFullName: "TRB Uluslararası Belgelendirme",
      email: "info@surdurulebilirdenetim.com",
      website: "trb.com.tr",
      address: "Türkiye"
    }
  };
  
  const content = isRoyalCert ? tenantContent.royalcert : tenantContent.trb;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(to bottom right, ${primaryColor}10, white, ${primaryColor}10)` }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50" style={{ borderColor: `${primaryColor}20` }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={logoUrl} alt={companyName} className="h-10 w-auto" style={{ mixBlendMode: 'multiply' }} />
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Ana Sayfa
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <FileText className="w-8 h-8" style={{ color: primaryColor }} />
            <h1 className="text-3xl font-bold" style={{ color: primaryColor }}>Kullanım Koşulları</h1>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-sm text-gray-500 mb-6">Son güncelleme: 15 Şubat 2026</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>1. Kabul</h2>
              <p>
                {content.companyFullName} Denetim Yönetim Sistemi'ni ("Hizmet") kullanarak, bu Kullanım 
                Koşulları'nı kabul etmiş olursunuz. Bu koşulları kabul etmiyorsanız, lütfen 
                hizmeti kullanmayınız.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>2. Hizmet Tanımı</h2>
              <p>
                {content.companyFullName} Denetim Yönetim Sistemi, sürdürülebilir turizm denetim 
                süreçlerini yönetmek için tasarlanmış bir web uygulamasıdır. Hizmet şunları içerir:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Denetim planlama ve takibi</li>
                <li>Rapor oluşturma ve yönetimi</li>
                <li>Müşteri bilgi yönetimi</li>
                <li>Takvim entegrasyonu</li>
                <li>Belge depolama</li>
                <li>Sertifika yönetimi</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>3. Kullanıcı Hesapları</h2>
              <p>Hizmeti kullanmak için:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Doğru ve güncel bilgiler sağlamalısınız</li>
                <li>Hesap güvenliğinizi korumalısınız</li>
                <li>Hesabınızda gerçekleşen tüm etkinliklerden sorumlusunuz</li>
                <li>Şifrenizi veya erişim bilgilerinizi üçüncü kişilerle paylaşmamalısınız</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>4. Kabul Edilebilir Kullanım</h2>
              <p>Hizmeti kullanırken aşağıdakileri yapmayı kabul edersiniz:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Tüm geçerli yasalara ve düzenlemelere uymak</li>
                <li>Diğer kullanıcıların haklarına saygı göstermek</li>
                <li>Hizmeti yalnızca amaçlanan iş amaçları için kullanmak</li>
                <li>Zararlı içerik veya yazılım yüklememek</li>
                <li>Hizmetin güvenliğini tehlikeye atacak eylemlerden kaçınmak</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>5. Fikri Mülkiyet</h2>
              <p>
                Hizmet ve içeriği (yazılım, tasarım, logolar, metinler) {content.companyFullName}'nin 
                mülkiyetindedir ve telif hakkı yasalarıyla korunmaktadır. Kullanıcılar, sisteme 
                yükledikleri iş verilerinin mülkiyetini korurlar.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>6. Üçüncü Taraf Hizmetleri</h2>
              <p>
                Hizmetimiz Google Calendar ve Google Drive gibi üçüncü taraf hizmetleriyle 
                entegre olabilir. Bu hizmetleri kullanırken, ilgili üçüncü tarafların kullanım 
                koşullarına da tabi olursunuz.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>7. Hizmet Değişiklikleri</h2>
              <p>
                {content.companyFullName}, hizmeti herhangi bir zamanda değiştirme, askıya alma veya 
                sonlandırma hakkını saklı tutar. Önemli değişiklikler hakkında kullanıcıları 
                bilgilendirmek için makul çaba göstereceğiz.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>8. Sorumluluk Sınırlaması</h2>
              <p>
                Hizmet "olduğu gibi" sunulmaktadır. {content.companyFullName}, hizmetin kesintisiz 
                veya hatasız olacağını garanti etmez. Yasaların izin verdiği azami ölçüde, 
                dolaylı, arızi veya sonuç olarak ortaya çıkan zararlardan sorumlu değiliz.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>9. Gizlilik</h2>
              <p>
                Kişisel verilerinizin nasıl toplandığı ve kullanıldığı hakkında bilgi için 
                lütfen <Link to="/privacy" className="hover:underline" style={{ color: primaryColor }}>Gizlilik Politikamızı</Link> inceleyin.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>10. Uygulanacak Hukuk</h2>
              <p>
                Bu Kullanım Koşulları, Türkiye Cumhuriyeti yasalarına tabidir. Bu koşullardan 
                kaynaklanan anlaşmazlıklar için Türkiye mahkemeleri yetkilidir.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>11. İletişim</h2>
              <p>
                Bu Kullanım Koşulları hakkında sorularınız için bizimle iletişime geçebilirsiniz:
              </p>
              <p className="mt-3">
                <strong>E-posta:</strong> {content.email}<br />
                <strong>Web:</strong> {content.website}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>12. Değişiklikler</h2>
              <p>
                Bu Kullanım Koşulları'nı zaman zaman güncelleyebiliriz. Değişiklikler bu 
                sayfada yayınlandığında yürürlüğe girer. Hizmeti kullanmaya devam etmeniz, 
                güncellenmiş koşulları kabul ettiğiniz anlamına gelir.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12 text-white" style={{ backgroundColor: primaryColor }}>
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-white/70">
            © 2026 {content.companyFullName}. Tüm hakları saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
